import React, { useState, useEffect, useMemo } from "react";
import { Save } from "lucide-react";
import SlideOutPanel from "./SlideOutPanel";
import {
  TextInput,
  TextArea,
  FormSection,
  FieldRow,
  PitchArsenal,
  PitchSelectionGroup,
} from "../forms/FormFields";

const DEFAULT_FORM_DATA = {
  playerId: null,
  name: "",
  role: "",
  throws: "",
  pitchArsenal: [],
  firstPitchRHH: "",
  firstPitchLHH: "",
  twoStrikePitchRHH: "",
  twoStrikePitchLHH: "",
  timeToPlate: "",
  writeup: "",
};

const EditPitcherPanel = ({
  isOpen,
  onClose,
  onSubmit,
  editingPlayer,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  useEffect(() => {
    if (editingPlayer) {
      setFormData({
        playerId: editingPlayer.playerId || editingPlayer.id,
        name: editingPlayer.name || "",
        role: editingPlayer.role || "",
        throws: editingPlayer.throws || "",
        pitchArsenal: editingPlayer.pitchArsenal || [],
        firstPitchRHH: editingPlayer.firstPitchRHH || "",
        firstPitchLHH: editingPlayer.firstPitchLHH || "",
        twoStrikePitchRHH: editingPlayer.twoStrikePitchRHH || "",
        twoStrikePitchLHH: editingPlayer.twoStrikePitchLHH || "",
        timeToPlate: editingPlayer.timeToPlate || "",
        writeup: editingPlayer.writeup || "",
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [editingPlayer, isOpen]);

  const pitchOptions = useMemo(() => {
    return formData.pitchArsenal
      .filter((p) => p.name && p.name.trim())
      .map((p) => p.name.trim());
  }, [formData.pitchArsenal]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleClose = () => {
    setFormData(DEFAULT_FORM_DATA);
    onClose();
  };

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Pitcher"
      width="w-[700px]"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <FormSection title="Basic Information">
          <TextInput
            label="Name"
            value={formData.name}
            onChange={(v) => handleChange("name", v)}
            disabled
          />
          <FieldRow>
            <TextInput
              label="Role"
              value={formData.role}
              onChange={(v) => handleChange("role", v)}
              placeholder="e.g., Starter, Reliever, Closer"
              className="flex-1"
            />
            <TextInput
              label="Throws"
              value={formData.throws}
              onChange={(v) => handleChange("throws", v)}
              placeholder="L or R"
              className="w-24"
            />
          </FieldRow>
        </FormSection>

        {/* Pitch Arsenal */}
        <FormSection title="Pitch Arsenal">
          <PitchArsenal
            pitches={formData.pitchArsenal}
            onChange={(pitches) => handleChange("pitchArsenal", pitches)}
          />
        </FormSection>

        {/* Pitch Tendencies */}
        <FormSection title="Pitch Tendencies">
          <PitchSelectionGroup
            label="First Pitch"
            valueRHH={formData.firstPitchRHH}
            valueLHH={formData.firstPitchLHH}
            onChangeRHH={(v) => handleChange("firstPitchRHH", v)}
            onChangeLHH={(v) => handleChange("firstPitchLHH", v)}
            pitchOptions={pitchOptions}
          />
          <PitchSelectionGroup
            label="Pitch with Two Strikes"
            valueRHH={formData.twoStrikePitchRHH}
            valueLHH={formData.twoStrikePitchLHH}
            onChangeRHH={(v) => handleChange("twoStrikePitchRHH", v)}
            onChangeLHH={(v) => handleChange("twoStrikePitchLHH", v)}
            pitchOptions={pitchOptions}
          />
        </FormSection>

        {/* Timing */}
        <FormSection title="Timing">
          <TextInput
            label="Time to Plate"
            value={formData.timeToPlate}
            onChange={(v) => handleChange("timeToPlate", v)}
            placeholder="e.g., 1.3s"
            className="w-32"
          />
        </FormSection>

        {/* Write-up */}
        <FormSection title="Write-up">
          <TextArea
            label="Scouting Notes"
            value={formData.writeup}
            onChange={(v) => handleChange("writeup", v)}
            rows={5}
            placeholder="Enter scouting notes, observations, strengths, weaknesses..."
          />
        </FormSection>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Save size={16} />
            Save Pitcher
          </button>
        </div>
      </form>
    </SlideOutPanel>
  );
};

export default EditPitcherPanel;

